import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Application,
  Container,
  Graphics,
  Sprite,
  Text,
  Texture,
  type Ticker,
} from 'pixi.js';
import { playSfx } from '../effects/sound.js';
import { usePixiScene } from './pixi/usePixiScene.js';
import { mixColor, originalTheme, sceneColor } from '../theme.js';
import type { SceneProps } from './types.js';
import { preferredDensity, preloadPieces, type PieceImages } from '../assets/sprites.js';
import { makeScratchBrush, stampPlan } from './scratch/scratchBrush.js';
import { FRAME_MARGIN_TILES, SCRATCH_PIECES, gridLayerRect, itemPiece, peelTriangle, placeInTile, type ScratchPiece } from './scratch/scratchArt.js';

interface ScratchPresentation {
  grid: string[][];
  win: boolean;
  prizeSymbol?: string;
}

interface ScratchPresenterConfig {
  rows: number;
  cols: number;
  symbols: string[];
}

const PRIMARY_FALLBACK = 0x3a82f6;

const GAP_RATIO = 0.1; // gap between tiles, as a fraction of tile size
const SYMBOL_REGION = 0.4; // central box of each cell (its symbol core) where scratch coverage is sampled; everything outside — corners a round coin can't reach, and the symbol's thin outer edges — never gates the round
const TILE_POP_THRESHOLD = 0.5; // fraction of a cell's SYMBOL_REGION that must be cleared before its symbol reads as visible; crossing it pops the cell open AND marks it done. The card reveals once EVERY cell has popped — never from whole-grid coverage, so clearing a couple of rows leaves the rest unpopped and unrevealed.
const SCRATCH_SFX_THROTTLE_MS = 100;
const IDLE_AUTO_REVEAL_MS = 6000; // never-stuck safety auto-reveal
const TURBO_AUTO_REVEAL_MS = 350; // auto-bet: start scratching the card by itself after this
// The coin scratching the card by itself (auto-bet, Reveal all, the idle safety net): how long it
// takes to sweep every row, so the player sees the scratch rather than a fade.
const AUTO_SCRATCH_MS = 1100;
const AUTO_SCRATCH_TURBO_MS = 800;
// Each footprint clears this much of the foil under it; overlapping ones clear the middle of a
// stroke in one pass and leave torn edges and streaks a second pass rubs off.
const SCRAPE_ALPHA = 0.65;
const FOIL_FADE_MS = 250; // foil fade-out on finish
const TURBO_DURATION_SCALE = 0.3;
const COVERAGE_SAMPLE = 96; // downscaled side used to estimate per-cell erased fraction; fine enough that the still-opaque gap foil doesn't bleed into a cell's edge samples and depress its reading
const SAMPLE_EVERY_MOVES = 3; // sample the foil alpha every Nth move event
const WIN_POP_DURATION_MS = 460;
const SPARKLE_COUNT = 7;
const SPARKLE_LIFE_MS = 620;
const FLAKE_CAP = 240; // live foil-shaving particles
const FLAKE_PER_PX = 1 / 6; // flakes spawned per scratched px
const FLAKE_PER_MOVE_MAX = 9;
const FLAKE_GRAVITY = 0.0016; // px/ms^2 — shavings arc up briefly, then drop off the card
const FOIL_COLORS = ['#cfd6e0', '#aab2bf', '#c6cdd8', '#8d94a1', '#e3e8f0'];

// With the 3D art: what makes opening a cell and winning feel like the card.
const POP_GLINTS = 5; // sparkles a popped cell throws off
const GLINT_LIFE_MS = 520;
const FOIL_TWINKLE_EVERY_MS = 520; // a glint crosses the unscratched foil now and then
const BURST_PER_WIN_CELL = 7; // coins and clovers each winning cell throws out
const BURST_LIFE_MS = 1100;
const BURST_GRAVITY = 0.0018; // px/ms^2
const WIN_DIM_ALPHA = 0.74; // the cells that did not win step back on a winning card
const WIN_DIM_SCALE = 0.9;

// The scratching tool is a large rendered coin that follows the pointer on the
// debris canvas (a CSS cursor caps at ~32px and never shows on touch).
const COIN_R_MIN = 20;
const COIN_R_MAX = 42;
const COIN_R_TILE_RATIO = 0.3;

const SYMBOL_COLOR: Record<string, number> = {
  star: 0xf5c518,
  gem: 0x22d3ee,
  coin: 0xfacc15,
  cherry: 0xef4444,
  bell: 0xf59e0b,
  seven: 0xa855f7,
  lemon: 0xcddc39,
};

function buzz(pattern: number | number[]) {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // haptics are best-effort
  }
}

function parseHexColor(input: string | undefined, fallback: number): number {
  if (!input) return fallback;
  const hex = input.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(hex)) return parseInt(hex, 16);
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    const r = hex[0];
    const g = hex[1];
    const b = hex[2];
    return parseInt(`${r}${r}${g}${g}${b}${b}`, 16);
  }
  return fallback;
}

function symbolColor(symbol: string): number {
  return SYMBOL_COLOR[symbol] ?? 0x9aa2ad;
}

function lighten(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return (lr << 16) | (lg << 8) | lb;
}

function darken(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  return (
    (Math.round(r * (1 - amount)) << 16) |
    (Math.round(g * (1 - amount)) << 8) |
    Math.round(b * (1 - amount))
  );
}

function readPresenter(config: unknown): ScratchPresenterConfig {
  const obj =
    config != null && typeof config === 'object'
      ? (config as Record<string, unknown>)
      : {};
  const rows = typeof obj.rows === 'number' ? obj.rows : 3;
  const cols = typeof obj.cols === 'number' ? obj.cols : 3;
  const symbols = Array.isArray(obj.symbols)
    ? obj.symbols.filter((s): s is string => typeof s === 'string')
    : [];
  return { rows, cols, symbols };
}

function readPresentation(presentation: unknown): ScratchPresentation | null {
  if (presentation == null || typeof presentation !== 'object') return null;
  const obj = presentation as Record<string, unknown>;
  if (!Array.isArray(obj.grid)) return null;
  const grid = obj.grid
    .filter((r): r is unknown[] => Array.isArray(r))
    .map((r) => r.map((c) => (typeof c === 'string' ? c : '')));
  return {
    grid,
    win: obj.win === true,
    prizeSymbol: typeof obj.prizeSymbol === 'string' ? obj.prizeSymbol : undefined,
  };
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function starPath2d(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
) {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? r : r * 0.45;
    const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
    const x = cx + Math.cos(a) * rad;
    const y = cy + Math.sin(a) * rad;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

// The tool coin is held tilted into the card like a real scratch: the face is
// foreshortened to an ellipse and the bottom rim's thickness digs into the
// surface at the contact point (the sprite's anchor).
const COIN_SQUASH = 0.58;
const COIN_THICK_RATIO = 0.26;

function coinSpritePad(r: number): number {
  return Math.ceil(r * 0.2);
}

function coinSpriteCssSize(r: number): { w: number; h: number } {
  const pad = coinSpritePad(r);
  return {
    w: r * 2 + pad * 2,
    h: r * 2 * COIN_SQUASH + r * COIN_THICK_RATIO + pad * 2,
  };
}

// Pre-rendered tilted gold coin: dark underside rim with reeded edge ticks,
// radial-lit foreshortened face, embossed inner ring + star relief, specular.
function buildCoinSprite(r: number, dpr: number): HTMLCanvasElement {
  const squash = COIN_SQUASH;
  const thick = r * COIN_THICK_RATIO;
  const pad = coinSpritePad(r);
  const { w: cssW, h: cssH } = coinSpriteCssSize(r);
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.ceil(cssW * dpr));
  c.height = Math.max(1, Math.ceil(cssH * dpr));
  const ctx = c.getContext('2d');
  if (!ctx) return c;
  ctx.scale(dpr, dpr);
  const cx = cssW / 2;
  const cy = pad + r * squash;

  ctx.beginPath();
  ctx.ellipse(cx, cy + thick, r, r * squash, 0, 0, Math.PI * 2);
  ctx.fillStyle = '#7c5305';
  ctx.fill();

  // reeding on the visible bottom rim, fading out toward the sides
  ctx.strokeStyle = 'rgba(74,48,4,0.8)';
  ctx.lineWidth = Math.max(1, r * 0.05);
  const edgeTicks = 26;
  for (let i = 0; i <= edgeTicks; i++) {
    const a = (i / edgeTicks) * Math.PI;
    const ex = cx + Math.cos(a) * r;
    const ey = cy + Math.sin(a) * r * squash;
    ctx.beginPath();
    ctx.moveTo(ex, ey);
    ctx.lineTo(ex, ey + thick * (0.35 + 0.65 * Math.sin(a)));
    ctx.stroke();
  }

  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(1, squash);

  const face = ctx.createRadialGradient(-r * 0.45, -r * 0.5, r * 0.1, 0, 0, r * 1.05);
  face.addColorStop(0, '#ffedb0');
  face.addColorStop(0.35, '#f8d34a');
  face.addColorStop(0.7, '#eab308');
  face.addColorStop(1, '#b07a08');
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fillStyle = face;
  ctx.fill();

  const faceTicks = 44;
  ctx.strokeStyle = 'rgba(120,80,8,0.55)';
  ctx.lineWidth = Math.max(1, r * 0.05);
  for (let i = 0; i < faceTicks; i++) {
    const a = (i / faceTicks) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * r * 0.92, Math.sin(a) * r * 0.92);
    ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    ctx.stroke();
  }

  ctx.beginPath();
  ctx.arc(0, 0, r * 0.99, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(130,86,7,0.9)';
  ctx.lineWidth = Math.max(1.5, r * 0.07);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(0, r * 0.025, r * 0.66, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(126,82,6,0.8)';
  ctx.lineWidth = Math.max(1, r * 0.05);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(0, -r * 0.025, r * 0.66, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(255,240,180,0.6)';
  ctx.lineWidth = Math.max(1, r * 0.04);
  ctx.stroke();

  starPath2d(ctx, 0, r * 0.04, r * 0.36);
  ctx.fillStyle = 'rgba(120,78,6,0.85)';
  ctx.fill();
  starPath2d(ctx, 0, -r * 0.04, r * 0.36);
  ctx.fillStyle = 'rgba(255,242,190,0.9)';
  ctx.fill();
  starPath2d(ctx, 0, 0, r * 0.36);
  ctx.fillStyle = '#edbf2a';
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(-r * 0.35, -r * 0.45, r * 0.42, r * 0.16, -0.6, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.38)';
  ctx.fill();

  ctx.restore();
  return c;
}

interface Sparkle {
  angle: number;
  dist: number;
  delay: number;
}

interface Flake {
  x: number;
  y: number;
  vx: number; // px/ms
  vy: number;
  rot: number;
  vr: number;
  size: number;
  life: number;
  maxLife: number;
  color: string;
  kind: 'curl' | 'chip'; // curled foil shaving vs flat fleck
  span: number; // curl arc length, radians
  glint: boolean;
}

interface Tile {
  index: number;
  symbol: string;
  win: boolean;
  container: Container; // positioned wrapper, target of pop scale
  panel: Graphics; // revealed background
  glow: Sprite | null; // the warm light behind a winning item, when the 3D art is in
  symbolGfx: Container; // pixi-drawn symbol
  ring: Graphics; // win glow ring
  sparkleGfx: Graphics; // win sparkle burst
  sparkles: Sparkle[];
  popElapsed: number;
  popping: boolean;
  popped: boolean; // tile crossed its own scratch threshold mid-round
  winElapsed: number;
  winPlaying: boolean;
  winSfxPlayed: boolean;
}

interface Glint {
  x: number;
  y: number;
  size: number;
  life: number;
  maxLife: number;
  rot: number;
}


interface BurstPiece {
  sprite: Sprite;
  vx: number; // px/ms
  vy: number;
  vr: number;
  life: number;
}

interface SceneState {
  phase: SceneProps['phase'];
  envelope: SceneProps['envelope'];
  onRevealComplete: () => void;
  turbo: boolean;
}

export function ScratchScene({
  config,
  envelope,
  phase,
  onRevealComplete,
  turbo = false,
}: SceneProps) {
  const { t } = useTranslation('originals');

  const presenter = useMemo(
    () => readPresenter(config.presenterConfig),
    [config.presenterConfig],
  );

  const stateRef = useRef<SceneState>({ phase, envelope, onRevealComplete, turbo });
  stateRef.current = { phase, envelope, onRevealComplete, turbo };

  const presenterRef = useRef(presenter);
  presenterRef.current = presenter;

  const revealAllLabel = t('scratch.revealAll');
  const scratchHintLabel = t('scratch.reveal');
  const labelRef = useRef(scratchHintLabel);
  labelRef.current = scratchHintLabel;

  // The React-managed foil canvas overlay. Sized to match the Pixi canvas.
  const foilCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Foil shavings layer drawn above the foil (the foil itself is erased with
  // destination-out, so debris can't live on the same bitmap).
  const flakesCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Exposed by the scene so the HTML "Reveal all" button triggers the proper
  // fade + win-effect + onRevealComplete sequence (never a bare phase flip).
  const revealAllRef = useRef<() => void>(() => {});

  const onReady = useRef((app: Application, host: HTMLDivElement) => {
    const primary = parseHexColor(
      getComputedStyle(host).getPropertyValue('--primary').trim() || undefined,
      PRIMARY_FALLBACK,
    );

    // Symbol wells in the tile's own green rather than a neutral slate.
    const theme = originalTheme('scratch');
    const panelBase = theme ? mixColor(sceneColor(theme.panel), 0x000000, 0.35) : 0x161a22;
    const panelTones = theme
      ? { base: panelBase, win: mixColor(panelBase, 0xffffff, 0.06), edge: sceneColor(theme.panel) }
      : { base: 0x161a22, win: 0x1c2230, edge: 0x2a313d };

    // The hint printed on the foil is a readout, so it wears the site's theme, not the card's colours.
    const hostStyle = getComputedStyle(host);
    const hintBg = hostStyle.getPropertyValue('--bg-raised').trim() || '#1a2331';
    const hintText = hostStyle.getPropertyValue('--text-primary').trim() || '#f4f6f8';

    const root = new Container();
    app.stage.addChild(root);

    // With the 3D art, the gold ticket sits under the grid.
    const frameSprite = new Sprite(Texture.EMPTY);
    frameSprite.visible = false;
    root.addChild(frameSprite);
    let tex: Record<ScratchPiece, Texture> | null = null;
    let artImgs: PieceImages<ScratchPiece> | null = null;
    let disposed = false;
    const artOn = () => tex !== null && rows === 3 && cols === 3;

    const gridLayer = new Container();
    root.addChild(gridLayer);

    // The coins and clovers a winning card throws out, over the ticket.
    const fxLayer = new Container();
    root.addChild(fxLayer);
    const burst: BurstPiece[] = [];

    // Idle sheen sweep overlay (one diagonal band swept across an idle grid).
    const idleSheen = new Graphics();
    idleSheen.visible = false;
    root.addChild(idleSheen);

    let tiles: Tile[] = [];
    let rows = presenterRef.current.rows;
    let cols = presenterRef.current.cols;
    let tileSize = 64;
    let gridW = 0;
    let gridH = 0;
    let gridOriginX = 0;
    let gridOriginY = 0;
    let builtRoundKey: string | null = null;
    let firedRoundId: string | null = null;
    let idlePhaseTime = 0;

    let reduced = prefersReducedMotion();

    // --- Foil overlay (HTML canvas 2D) state ---
    let foilCanvas: HTMLCanvasElement | null = null;
    let foilCtx: CanvasRenderingContext2D | null = null;
    let foilDpr = 1;
    let foilCssW = 0;
    let foilCssH = 0;
    let foilActive = false; // true while the foil is shown over a playable round
    let foilFadeFrom = 1;
    let foilFadeElapsed = 0;
    let foilFading = false;
    let foilFadeMs = FOIL_FADE_MS;
    let revealStarted = false; // win effects + onRevealComplete scheduling started
    let scratching = false;
    let scratchPointerId: number | null = null;
    let lastScratchX = 0;
    let lastScratchY = 0;
    let lastScratchSfx = 0;
    let moveCounter = 0;
    let poppedCount = 0; // tiles individually scratched open this round
    let prizePopped = 0; // popped tiles that carry the prize symbol (3 = found the win)

    // Wall-clock timers, NOT ticker-driven: a hidden/occluded tab freezes rAF and
    // any deltaMS-accumulated timeout with it (the CardsScene rAF-stall pattern),
    // which would leave the round stuck in 'playing' forever.
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    let finishTimer: ReturnType<typeof setTimeout> | null = null;

    function clearIdleTimer() {
      if (idleTimer != null) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }
    }

    function clearFinishTimer() {
      if (finishTimer != null) {
        clearTimeout(finishTimer);
        finishTimer = null;
      }
    }

    // (Re)arm the never-stuck auto-reveal; reset on every scratch interaction.
    function armIdleTimer() {
      clearIdleTimer();
      const ms = stateRef.current.turbo ? TURBO_AUTO_REVEAL_MS : IDLE_AUTO_REVEAL_MS;
      idleTimer = setTimeout(() => startAutoScratch(), ms);
    }

    // --- Foil shavings + tool coin (debris canvas above the foil) ---
    let flakesCtx: CanvasRenderingContext2D | null = null;
    const flakes: Flake[] = [];
    const glints: Glint[] = [];
    let twinkleClock = 0;
    let foilIdle = false; // the idle ticket shows its foil, not yet scratchable
    // The coin scratching the card by itself (startAutoScratch): back and forth along every
    // row, two passes a row, through the same scraper, shavings and sound as a hand.
    let autoScratch: { path: [number, number][]; elapsed: number; duration: number; done: number } | null = null;

    // Tool-coin pointer state. The coin floats slightly while hovering and
    // presses down (closer shadow, squashed scale, grit jitter) while scratching.
    let coinOver = false;
    let coinX = 0;
    let coinY = 0;
    let coinTilt = 0;
    let coinTiltTarget = 0;
    let coinPress = 0;
    let coinSprite: HTMLCanvasElement | null = null;
    let coinSpriteKey = '';
    let coinSpriteW = 0;
    let coinSpriteH = 0;

    function toolCoinRadius(): number {
      return clamp(tileSize * COIN_R_TILE_RATIO, COIN_R_MIN, COIN_R_MAX);
    }

    function syncFlakesSize() {
      const c = flakesCanvasRef.current;
      if (!c) return;
      const pxW = Math.max(1, Math.round(foilCssW * foilDpr));
      const pxH = Math.max(1, Math.round(foilCssH * foilDpr));
      if (c.width !== pxW || c.height !== pxH) {
        c.width = pxW;
        c.height = pxH;
      }
      c.style.width = `${foilCssW}px`;
      c.style.height = `${foilCssH}px`;
      flakesCtx = c.getContext('2d');
      flakesCtx?.setTransform(foilDpr, 0, 0, foilDpr, 0, 0);
    }

    function spawnFlakes(x: number, y: number, dx: number, dy: number, count: number) {
      if (reduced) return;
      for (let i = 0; i < count; i++) {
        if (flakes.length >= FLAKE_CAP) flakes.shift();
        const along = Math.random() * 0.05;
        flakes.push({
          x: x + (Math.random() - 0.5) * 14,
          y: y + (Math.random() - 0.5) * 8,
          vx: dx * along + (Math.random() - 0.5) * 0.16,
          vy: dy * along * 0.3 - 0.1 + Math.random() * 0.22,
          rot: Math.random() * Math.PI * 2,
          vr: (Math.random() - 0.5) * 0.025,
          size: 2.4 + Math.random() * 4.6,
          life: 0,
          maxLife: 900 + Math.random() * 700,
          color: FOIL_COLORS[Math.floor(Math.random() * FOIL_COLORS.length)]!,
          kind: Math.random() < 0.6 ? 'curl' : 'chip',
          span: 1.1 + Math.random() * 1.4,
          glint: Math.random() < 0.18,
        });
      }
    }

    function stepFlakes(ctx: CanvasRenderingContext2D, dt: number) {
      for (let i = flakes.length - 1; i >= 0; i--) {
        const f = flakes[i]!;
        f.life += dt;
        if (f.life >= f.maxLife || f.y > foilCssH + 16) {
          flakes.splice(i, 1);
          continue;
        }
        f.vy += FLAKE_GRAVITY * dt;
        f.vx *= Math.pow(0.9985, dt);
        f.x += f.vx * dt;
        f.y += f.vy * dt;
        f.rot += f.vr * dt;
        const t = f.life / f.maxLife;
        const fade = t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
        ctx.save();
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rot);
        ctx.globalAlpha = 0.95 * fade;
        if (f.kind === 'curl') {
          ctx.strokeStyle = f.color;
          ctx.lineWidth = Math.max(1, f.size * 0.4);
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.arc(0, 0, f.size * 0.55, 0, f.span);
          ctx.stroke();
          if (f.glint) {
            ctx.strokeStyle = 'rgba(255,255,255,0.85)';
            ctx.lineWidth = Math.max(0.6, f.size * 0.16);
            ctx.beginPath();
            ctx.arc(0, 0, f.size * 0.55, f.span * 0.15, f.span * 0.5);
            ctx.stroke();
          }
        } else {
          ctx.fillStyle = f.color;
          ctx.fillRect(-f.size / 2, -f.size / 2, f.size, f.size * 0.62);
          if (f.glint) {
            ctx.fillStyle = 'rgba(255,255,255,0.7)';
            ctx.fillRect(-f.size / 2, -f.size / 2, f.size, f.size * 0.18);
          }
        }
        ctx.restore();
      }
    }

    function drawToolCoin(ctx: CanvasRenderingContext2D, dt: number) {
      if (!coinOver || !foilActive || revealStarted) return;
      const r = toolCoinRadius();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const key = `${Math.round(r)}:${dpr}`;
      if (!coinSprite || coinSpriteKey !== key) {
        coinSprite = buildCoinSprite(r, dpr);
        coinSpriteKey = key;
        const css = coinSpriteCssSize(r);
        coinSpriteW = css.w;
        coinSpriteH = css.h;
      }

      coinTilt += (coinTiltTarget - coinTilt) * Math.min(1, dt / 90);
      coinTiltTarget *= Math.pow(0.995, dt);
      const pressTarget = scratching ? 1 : 0;
      coinPress += (pressTarget - coinPress) * Math.min(1, dt / 80);

      // hovering, the coin floats a touch above the card; pressing plants its
      // bottom rim on the contact point
      const lift = (1 - coinPress) * r * 0.16;
      const scale = 1 - coinPress * 0.05;

      const shAlpha = 0.14 + 0.18 * coinPress;
      ctx.save();
      ctx.beginPath();
      ctx.ellipse(coinX, coinY + 2, r * 0.9, r * 0.22, 0, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(8,10,14,${(shAlpha * 0.5).toFixed(3)})`;
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(coinX, coinY + 2, r * 0.6, r * 0.14, 0, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(8,10,14,${shAlpha.toFixed(3)})`;
      ctx.fill();
      ctx.restore();

      const jx = scratching && !reduced ? (Math.random() - 0.5) * 1.6 : 0;
      const jy = scratching && !reduced ? (Math.random() - 0.5) * 1.6 : 0;
      ctx.save();
      // rotate around the contact point so the coin leans into the stroke
      ctx.translate(coinX + jx, coinY - lift + jy);
      ctx.rotate(coinTilt);
      ctx.scale(scale, scale);
      const pad = coinSpritePad(r);
      ctx.drawImage(
        coinSprite,
        -coinSpriteW / 2,
        -(coinSpriteH - pad),
        coinSpriteW,
        coinSpriteH,
      );
      ctx.restore();
    }

    function spawnGlint(x: number, y: number, size: number, delay = 0) {
      if (reduced || !artImgs) return;
      glints.push({ x, y, size, life: -delay, maxLife: GLINT_LIFE_MS, rot: Math.random() * Math.PI });
    }

    function stepGlints(ctx: CanvasRenderingContext2D, dt: number) {
      const img = artImgs?.sparkle;
      if (!img || glints.length === 0) return;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      for (let i = glints.length - 1; i >= 0; i--) {
        const g = glints[i]!;
        g.life += dt;
        if (g.life >= g.maxLife) {
          glints.splice(i, 1);
          continue;
        }
        if (g.life < 0) continue;
        const a = Math.sin((Math.PI * g.life) / g.maxLife);
        const size = g.size * (0.45 + 0.55 * a);
        ctx.globalAlpha = a;
        ctx.setTransform(foilDpr, 0, 0, foilDpr, 0, 0);
        ctx.translate(g.x, g.y);
        ctx.rotate(g.rot + g.life * 0.0015);
        ctx.drawImage(img, -size / 2, -size / 2, size, size);
      }
      ctx.restore();
    }

    // Now and then a glint crosses the silver, only where the foil is still whole.
    function twinkleFoil(dt: number) {
      if (reduced || !artImgs || !artOn() || revealStarted || !(foilActive || foilIdle)) return;
      twinkleClock += dt;
      if (twinkleClock < FOIL_TWINKLE_EVERY_MS) return;
      twinkleClock = 0;
      const x = gridOriginX + gridW * (0.08 + Math.random() * 0.84);
      const y = gridOriginY + gridH * (0.08 + Math.random() * 0.84);
      const px = foilCtx?.getImageData(Math.round(x * foilDpr), Math.round(y * foilDpr), 1, 1).data[3] ?? 0;
      if (px > 200) spawnGlint(x, y, tileSize * 0.34);
    }

    function drawOverlay(dt: number) {
      const ctx = flakesCtx;
      if (!ctx) return;
      ctx.clearRect(0, 0, foilCssW, foilCssH);
      stepAutoScratch(dt);
      stepFlakes(ctx, dt);
      twinkleFoil(dt);
      stepGlints(ctx, dt);
      drawToolCoin(ctx, dt);
    }

    function tileCentre(index: number): { x: number; y: number } {
      const gap = tileSize * GAP_RATIO;
      return {
        x: gridOriginX + (index % cols) * (tileSize + gap) + tileSize / 2,
        y: gridOriginY + Math.floor(index / cols) * (tileSize + gap) + tileSize / 2,
      };
    }

    function spawnBurst(tile: Tile) {
      if (!tex || reduced) return;
      const c = tileCentre(tile.index);
      const k = tileSize / 130;
      for (let i = 0; i < BURST_PER_WIN_CELL; i++) {
        const t = i % 2 === 0 ? tex['item-coin'] : tex['item-clover'];
        const sprite = new Sprite(t);
        sprite.anchor.set(0.5);
        const side = tileSize * (0.26 + Math.random() * 0.14);
        sprite.scale.set(side / Math.max(t.width, t.height));
        sprite.position.set(c.x, c.y);
        sprite.rotation = Math.random() * Math.PI * 2;
        fxLayer.addChild(sprite);
        const a = -Math.PI / 2 + (Math.random() - 0.5) * 2.2;
        const v = (0.42 + Math.random() * 0.38) * k;
        burst.push({ sprite, vx: Math.cos(a) * v, vy: Math.sin(a) * v, vr: (Math.random() - 0.5) * 0.012, life: 0 });
      }
    }

    function stepBurst(dt: number) {
      for (let i = burst.length - 1; i >= 0; i--) {
        const b = burst[i]!;
        b.life += dt;
        if (b.life >= BURST_LIFE_MS) {
          b.sprite.destroy();
          burst.splice(i, 1);
          continue;
        }
        b.vy += BURST_GRAVITY * dt;
        b.sprite.x += b.vx * dt;
        b.sprite.y += b.vy * dt;
        b.sprite.rotation += b.vr * dt;
        const t = b.life / BURST_LIFE_MS;
        b.sprite.alpha = t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
      }
    }

    function clearBurst() {
      for (const b of burst) b.sprite.destroy();
      burst.length = 0;
    }

    function drawStar(g: Graphics, r: number, color: number) {
      const pts: number[] = [];
      for (let i = 0; i < 10; i++) {
        const rad = i % 2 === 0 ? r : r * 0.45;
        const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
        pts.push(Math.cos(a) * rad, Math.sin(a) * rad);
      }
      g.poly(pts).fill({ color });
      g.poly(pts).stroke({ width: r * 0.08, color: darken(color, 0.35), alpha: 0.6 });
    }

    function drawCoin(g: Graphics, r: number, color: number) {
      const R = r * 1.14;
      const starPts = (cx: number, cy: number, sr: number) => {
        const pts: number[] = [];
        for (let i = 0; i < 10; i++) {
          const rad = i % 2 === 0 ? sr : sr * 0.45;
          const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
          pts.push(cx + Math.cos(a) * rad, cy + Math.sin(a) * rad);
        }
        return pts;
      };
      g.circle(0, R * 0.07, R).fill({ color: darken(color, 0.45) });
      g.circle(0, 0, R).fill({ color });
      g.circle(0, 0, R).stroke({ width: R * 0.09, color: darken(color, 0.32) });
      for (let i = 0; i < 20; i++) {
        const a = (i / 20) * Math.PI * 2;
        g.moveTo(Math.cos(a) * R * 0.88, Math.sin(a) * R * 0.88)
          .lineTo(Math.cos(a) * R * 0.98, Math.sin(a) * R * 0.98)
          .stroke({ width: R * 0.045, color: darken(color, 0.35), alpha: 0.7 });
      }
      g.circle(0, R * 0.03, R * 0.62).stroke({
        width: R * 0.06,
        color: darken(color, 0.28),
        alpha: 0.9,
      });
      g.circle(0, -R * 0.03, R * 0.62).stroke({
        width: R * 0.045,
        color: lighten(color, 0.4),
        alpha: 0.85,
      });
      g.poly(starPts(0, R * 0.04, R * 0.34)).fill({ color: darken(color, 0.38), alpha: 0.9 });
      g.poly(starPts(0, -R * 0.04, R * 0.34)).fill({
        color: lighten(color, 0.45),
        alpha: 0.9,
      });
      g.poly(starPts(0, 0, R * 0.34)).fill({ color: lighten(color, 0.12) });
      g.ellipse(-R * 0.34, -R * 0.4, R * 0.34, R * 0.13).fill({
        color: 0xffffff,
        alpha: 0.35,
      });
    }

    function drawGem(g: Graphics, r: number, color: number) {
      const top = -r;
      const bottom = r;
      const w = r * 0.78;
      const shoulder = -r * 0.38;
      g.poly([0, top, w, shoulder, 0, bottom, -w, shoulder]).fill({ color });
      g.moveTo(-w, shoulder)
        .lineTo(w, shoulder)
        .stroke({ width: r * 0.06, color: lighten(color, 0.4), alpha: 0.8 });
      g.moveTo(0, top)
        .lineTo(0, bottom)
        .stroke({ width: r * 0.05, color: darken(color, 0.3), alpha: 0.5 });
    }

    function drawBell(g: Graphics, r: number, color: number) {
      const top = -r * 0.75;
      g.moveTo(-r * 0.62, r * 0.45)
        .lineTo(-r * 0.52, -r * 0.1)
        .arc(0, -r * 0.1, r * 0.52, Math.PI, 0)
        .lineTo(r * 0.62, r * 0.45)
        .closePath()
        .fill({ color });
      g.rect(-r * 0.72, r * 0.45, r * 1.44, r * 0.16).fill({ color: darken(color, 0.15) });
      g.circle(0, r * 0.78, r * 0.16).fill({ color: darken(color, 0.25) });
      g.circle(0, top, r * 0.12).fill({ color: darken(color, 0.2) });
    }

    function drawLemon(g: Graphics, r: number, color: number) {
      g.ellipse(0, 0, r * 1.02, r * 0.7).fill({ color });
      g.ellipse(0, 0, r * 1.02, r * 0.7).stroke({ width: r * 0.08, color: darken(color, 0.3), alpha: 0.7 });
      g.circle(-r * 1.02, 0, r * 0.13).fill({ color: darken(color, 0.18) });
      g.circle(r * 1.02, 0, r * 0.13).fill({ color: darken(color, 0.18) });
      g.ellipse(-r * 0.3, -r * 0.22, r * 0.3, r * 0.14).fill({ color: 0xffffff, alpha: 0.3 });
    }

    function drawCherry(g: Graphics, r: number, color: number) {
      const node = { x: r * 0.2, y: -r * 0.85 };
      g.moveTo(node.x, node.y)
        .lineTo(-r * 0.45, r * 0.25)
        .stroke({ width: r * 0.1, color: 0x2f7d32 });
      g.moveTo(node.x, node.y)
        .lineTo(r * 0.5, r * 0.1)
        .stroke({ width: r * 0.1, color: 0x2f7d32 });
      g.circle(-r * 0.45, r * 0.4, r * 0.38).fill({ color });
      g.circle(r * 0.5, r * 0.28, r * 0.38).fill({ color });
      g.circle(-r * 0.56, r * 0.28, r * 0.12).fill({ color: 0xffffff, alpha: 0.4 });
      g.circle(r * 0.4, r * 0.16, r * 0.12).fill({ color: 0xffffff, alpha: 0.4 });
    }

    function buildSymbol(symbol: string, size: number): Container {
      const wrap = new Container();
      const piece = itemPiece(symbol);
      if (tex && artOn() && piece) {
        wrap.addChild(placeInTile(tex[piece], piece, size));
        return wrap;
      }
      const g = new Graphics();
      const r = size * 0.26;
      const color = symbolColor(symbol);
      switch (symbol) {
        case 'star':
          drawStar(g, r, color);
          break;
        case 'coin':
          drawCoin(g, r, color);
          break;
        case 'gem':
          drawGem(g, r, color);
          break;
        case 'bell':
          drawBell(g, r, color);
          break;
        case 'cherry':
          drawCherry(g, r, color);
          break;
        case 'lemon':
          drawLemon(g, r, color);
          wrap.rotation = -0.32;
          break;
        case 'seven': {
          const sevenText = new Text({
            text: '7',
            style: {
              fontFamily: 'system-ui, sans-serif',
              fontSize: size * 0.5,
              fill: color,
              fontWeight: '900',
              fontStyle: 'italic',
            },
          });
          sevenText.anchor.set(0.5);
          wrap.addChild(sevenText);
          return wrap;
        }
        default: {
          const txt = new Text({
            text: symbol.slice(0, 2).toUpperCase(),
            style: {
              fontFamily: 'system-ui, sans-serif',
              fontSize: size * 0.28,
              fill: color,
              fontWeight: '900',
            },
          });
          txt.anchor.set(0.5);
          wrap.addChild(txt);
          return wrap;
        }
      }
      wrap.addChild(g);
      return wrap;
    }

    function drawPanel(panel: Graphics, size: number, win: boolean) {
      panel.clear();
      const radius = size * 0.16;
      panel
        .roundRect(-size / 2, -size / 2, size, size, radius)
        .fill({ color: win ? panelTones.win : panelTones.base });
      panel
        .roundRect(-size / 2, -size / 2, size, size, radius)
        .stroke({ width: Math.max(1, size * 0.02), color: panelTones.edge, alpha: 1 });
      panel
        .roundRect(
          -size / 2 + size * 0.06,
          -size / 2 + size * 0.06,
          size - size * 0.12,
          size * 0.4,
          radius * 0.6,
        )
        .fill({ color: 0xffffff, alpha: 0.03 });
    }

    function destroyTiles() {
      for (const tile of tiles) {
        tile.container.destroy({ children: true });
      }
      tiles = [];
      clearBurst();
      if (autoScratch) {
        autoScratch = null;
        scratching = false;
        coinOver = false;
      }
      glints.length = 0;
    }

    function buildTile(index: number, symbol: string, win: boolean, size: number): Tile {
      const container = new Container();

      const panel = new Graphics();
      let glow: Sprite | null = null;
      if (tex && artOn()) {
        // The ticket's paper (in the frame layer) is every cell's ground; a winner lights up on it.
        glow = new Sprite(tex.glow);
        glow.anchor.set(0.5);
        glow.width = glow.height = size * 1.35;
        // Added light only whitens cream paper; a gold-tinted halo reads as the card's warm glow on it.
        glow.tint = 0xffe27a;
        glow.visible = false;
        container.addChild(glow);
      } else {
        drawPanel(panel, size, win);
      }
      container.addChild(panel);

      const symbolGfx = buildSymbol(symbol, size);
      container.addChild(symbolGfx);

      const ring = new Graphics();
      container.addChild(ring);

      const sparkleGfx = new Graphics();
      container.addChild(sparkleGfx);

      const sparkles: Sparkle[] = [];
      for (let i = 0; i < SPARKLE_COUNT; i++) {
        sparkles.push({
          angle: (i / SPARKLE_COUNT) * Math.PI * 2 + index * 0.6,
          dist: size * (0.3 + (i % 2) * 0.12),
          delay: i * 0.03,
        });
      }

      return {
        index,
        symbol,
        win,
        container,
        panel,
        glow,
        symbolGfx,
        ring,
        sparkleGfx,
        sparkles,
        popElapsed: 0,
        popping: false,
        popped: false,
        winElapsed: 0,
        winPlaying: false,
        winSfxPlayed: false,
      };
    }

    function computeLayout() {
      const w = app.screen.width;
      const h = app.screen.height;
      // The ticket around the grid needs its own room inside the canvas.
      const margin = artOn() ? FRAME_MARGIN_TILES * 2 : 0;
      const cell = Math.min(
        w / (cols + (cols - 1) * GAP_RATIO + margin),
        h / (rows + (rows - 1) * GAP_RATIO + margin),
      );
      tileSize = Math.max(32, Math.floor(cell));
      const gap = tileSize * GAP_RATIO;
      gridW = cols * tileSize + (cols - 1) * gap;
      gridH = rows * tileSize + (rows - 1) * gap;
      gridOriginX = (w - gridW) / 2;
      gridOriginY = (h - gridH) / 2;
    }

    function relayout() {
      computeLayout();
      const gap = tileSize * GAP_RATIO;
      gridLayer.position.set(gridOriginX, gridOriginY);
      root.position.set(0, 0);
      frameSprite.visible = artOn();
      if (tex && artOn()) {
        const f = gridLayerRect('frame', gridOriginX, gridOriginY, gridW);
        frameSprite.texture = tex.frame;
        frameSprite.position.set(f.x, f.y);
        frameSprite.width = f.w;
        frameSprite.height = f.h;
      }
      for (const tile of tiles) {
        const r = Math.floor(tile.index / cols);
        const c = tile.index % cols;
        tile.container.position.set(
          c * (tileSize + gap) + tileSize / 2,
          r * (tileSize + gap) + tileSize / 2,
        );
      }
    }

    // --- Foil canvas painting -------------------------------------------------

    function syncFoilSize() {
      foilCanvas = foilCanvasRef.current;
      if (!foilCanvas) return;
      const rect = host.getBoundingClientRect();
      foilCssW = rect.width;
      foilCssH = rect.height;
      foilDpr = Math.min(window.devicePixelRatio || 1, 2);
      const pxW = Math.max(1, Math.round(foilCssW * foilDpr));
      const pxH = Math.max(1, Math.round(foilCssH * foilDpr));
      if (foilCanvas.width !== pxW || foilCanvas.height !== pxH) {
        foilCanvas.width = pxW;
        foilCanvas.height = pxH;
      }
      foilCanvas.style.width = `${foilCssW}px`;
      foilCanvas.style.height = `${foilCssH}px`;
      foilCtx = foilCanvas.getContext('2d');
      if (foilCtx) {
        foilCtx.setTransform(foilDpr, 0, 0, foilDpr, 0, 0);
      }
      syncFlakesSize();
    }

    function paintFoil(withHint = true) {
      const ctx = foilCtx;
      if (!ctx) return;
      ctx.setTransform(foilDpr, 0, 0, foilDpr, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, foilCssW, foilCssH);

      // The grid lives in Pixi screen-space; the foil canvas matches host CSS px.
      // Pixi autoDensity keeps screen size == CSS size, so coordinates align.
      const x0 = gridOriginX;
      const y0 = gridOriginY;
      const radius = tileSize * 0.16;

      if (artImgs && artOn()) {
        // The rendered silver sheet, with its bottom-right corner curled back off the paper.
        ctx.save();
        roundRectPath(ctx, x0, y0, gridW, gridH, radius);
        ctx.clip();
        const f = gridLayerRect('foil', x0, y0, gridW);
        ctx.drawImage(artImgs.foil, f.x, f.y, f.w, f.h);
        const tri = peelTriangle(x0, y0, gridW);
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.moveTo(tri[0]![0], tri[0]![1]);
        for (const [px, py] of tri.slice(1)) ctx.lineTo(px, py);
        ctx.closePath();
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
        const pr = gridLayerRect('peel', x0, y0, gridW);
        ctx.drawImage(artImgs.peel, pr.x, pr.y, pr.w, pr.h);
        ctx.restore();
      } else {
        ctx.save();
        roundRectPath(ctx, x0, y0, gridW, gridH, radius);
        ctx.clip();

        // metallic vertical gradient base
        const grad = ctx.createLinearGradient(0, y0, 0, y0 + gridH);
        grad.addColorStop(0, '#cfd6e0');
        grad.addColorStop(0.4, '#aab2bf');
        grad.addColorStop(0.55, '#c6cdd8');
        grad.addColorStop(1, '#8d94a1');
        ctx.fillStyle = grad;
        ctx.fillRect(x0, y0, gridW, gridH);

        // diagonal sheen band
        const sheen = ctx.createLinearGradient(x0, y0, x0 + gridW, y0 + gridH);
        sheen.addColorStop(0, 'rgba(255,255,255,0)');
        sheen.addColorStop(0.42, 'rgba(255,255,255,0)');
        sheen.addColorStop(0.5, 'rgba(255,255,255,0.34)');
        sheen.addColorStop(0.58, 'rgba(255,255,255,0)');
        sheen.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = sheen;
        ctx.fillRect(x0, y0, gridW, gridH);

        // diagonal hatch grain
        ctx.strokeStyle = 'rgba(255,255,255,0.08)';
        ctx.lineWidth = 1;
        const step = Math.max(5, tileSize * 0.11);
        for (let x = x0 - gridH; x < x0 + gridW; x += step) {
          ctx.beginPath();
          ctx.moveTo(x, y0);
          ctx.lineTo(x + gridH, y0 + gridH);
          ctx.stroke();
        }

        // sandy speckle noise so the surface reads as scratchable grit, not chrome
        const speckles = Math.round((gridW * gridH) / 220);
        for (let i = 0; i < speckles; i++) {
          const sx = x0 + Math.random() * gridW;
          const sy = y0 + Math.random() * gridH;
          ctx.fillStyle = Math.random() < 0.5 ? 'rgba(255,255,255,0.10)' : 'rgba(40,46,56,0.10)';
          ctx.fillRect(sx, sy, 1.2, 1.2);
        }
        ctx.restore();

        // faint cell separators so the grid reads as discrete tiles
        const gap = tileSize * GAP_RATIO;
        ctx.strokeStyle = 'rgba(60,68,80,0.45)';
        ctx.lineWidth = Math.max(1.5, gap * 0.6);
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const tx = x0 + c * (tileSize + gap);
            const ty = y0 + r * (tileSize + gap);
            roundRectPath(ctx, tx, ty, tileSize, tileSize, radius);
            ctx.stroke();
          }
        }

        // per-cell "?" print — the classic "something hides here" scratch-card mark
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `900 ${Math.max(14, Math.round(tileSize * 0.34))}px system-ui, sans-serif`;
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const cx = x0 + c * (tileSize + gap) + tileSize / 2;
            const cy = y0 + r * (tileSize + gap) + tileSize / 2;
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate((((r * cols + c) % 3) - 1) * 0.14);
            ctx.fillStyle = 'rgba(255,255,255,0.22)';
            ctx.fillText('?', 1.5, 1.5);
            ctx.fillStyle = 'rgba(52,60,72,0.42)';
            ctx.fillText('?', 0, 0);
            ctx.restore();
          }
        }
        ctx.restore();

        // outer foil edge
        ctx.strokeStyle = 'rgba(125,131,144,0.9)';
        ctx.lineWidth = Math.max(1, tileSize * 0.02);
        roundRectPath(ctx, x0, y0, gridW, gridH, radius);
        ctx.stroke();
      }

      // "scratch to reveal" hint pill, readable over the metallic base
      if (!withHint) return;
      ctx.save();
      const hint = labelRef.current;
      const fontPx = Math.max(11, Math.round(tileSize * 0.15));
      ctx.font = `700 ${fontPx}px system-ui, sans-serif`;
      const tw = ctx.measureText(hint).width;
      const px = x0 + gridW / 2;
      const py = y0 + gridH / 2;
      const padX = fontPx * 0.9;
      const pillH = fontPx * 2;
      roundRectPath(ctx, px - tw / 2 - padX, py - pillH / 2, tw + padX * 2, pillH, pillH / 2);
      ctx.globalAlpha = 0.86;
      ctx.fillStyle = hintBg;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.fillStyle = hintText;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(hint, px, py);
      ctx.restore();
    }

    function roundRectPath(
      ctx: CanvasRenderingContext2D,
      x: number,
      y: number,
      w: number,
      h: number,
      r: number,
    ) {
      const rr = Math.min(r, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + rr, y);
      ctx.arcTo(x + w, y, x + w, y + h, rr);
      ctx.arcTo(x + w, y + h, x, y + h, rr);
      ctx.arcTo(x, y + h, x, y, rr);
      ctx.arcTo(x, y, x + w, y, rr);
      ctx.closePath();
    }

    // Ragged stamped brush instead of a smooth stroke: jittered, size-varied dabs
    // along the segment plus stray flecks past the edge, so the cleared path has
    // the torn, gritty silhouette of a real coin scrape.
    // The coin's edge scraping the foil: a footprint wide across the drag, stamped along it
    // (scratchBrush.ts). A tap with no drag stamps once, at a random lean.
    const brush = makeScratchBrush(96);
    function eraseLine(x0: number, y0: number, x1: number, y1: number) {
      const ctx = foilCtx;
      if (!ctx) return;
      const width = Math.max(18, toolCoinRadius() * 1.3);
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      if (!brush) {
        ctx.lineCap = 'round';
        ctx.lineWidth = width * 0.6;
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1 + 0.01, y1);
        ctx.stroke();
        ctx.restore();
        return;
      }
      const plan = stampPlan(x0, y0, x1, y1, width);
      const angle = x0 === x1 && y0 === y1 ? Math.random() * Math.PI : plan.angle;
      ctx.globalAlpha = SCRAPE_ALPHA;
      for (const [x, y] of plan.points) {
        ctx.setTransform(foilDpr, 0, 0, foilDpr, 0, 0);
        ctx.translate(x, y);
        // a hair of wobble, like a hand that does not hold the coin perfectly still
        ctx.rotate(angle + (Math.random() - 0.5) * 0.08);
        ctx.drawImage(brush.canvas, -width / 2, -width / 2, width, width);
      }
      ctx.restore();
    }

    // Sample the foil's alpha on a downscaled copy and bucket the cleared pixels
    // per cell, counting ONLY the central SYMBOL_REGION box of each cell (where the
    // printed symbol sits). A cell "passes" the instant its symbol shape is exposed,
    // not when its empty corners are cleared — a round coin can't reach those, and
    // nothing is hidden there. Completion is still judged cell-by-cell (every cell's
    // centre must be cleared), never from aggregate whole-grid coverage, so clearing
    // a couple of rows leaves the rest unexposed.
    const sampleCanvas =
      typeof document !== 'undefined' ? document.createElement('canvas') : null;
    function erasedFractions(): number[] {
      const empty: number[] = [];
      const ctx = foilCtx;
      if (!ctx || !foilCanvas || !sampleCanvas || gridW <= 0 || gridH <= 0) return empty;
      const sw = COVERAGE_SAMPLE;
      const sh = COVERAGE_SAMPLE;
      sampleCanvas.width = sw;
      sampleCanvas.height = sh;
      const sctx = sampleCanvas.getContext('2d');
      if (!sctx) return empty;
      // map grid rect (css px) → source px on the foil bitmap
      const srcX = gridOriginX * foilDpr;
      const srcY = gridOriginY * foilDpr;
      const srcW = gridW * foilDpr;
      const srcH = gridH * foilDpr;
      sctx.clearRect(0, 0, sw, sh);
      sctx.drawImage(foilCanvas, srcX, srcY, srcW, srcH, 0, 0, sw, sh);
      const data = sctx.getImageData(0, 0, sw, sh).data;
      const gap = tileSize * GAP_RATIO;
      const cellSpan = tileSize + gap;
      const lo = (tileSize * (1 - SYMBOL_REGION)) / 2; // inset to the symbol band
      const hi = tileSize - lo;
      const tileClear: number[] = new Array(rows * cols).fill(0);
      const tilePx: number[] = new Array(rows * cols).fill(0);
      for (let py = 0; py < sh; py++) {
        const gy = ((py + 0.5) / sh) * gridH; // grid-space css px
        const r = Math.min(rows - 1, Math.floor(gy / cellSpan));
        const ly = gy - r * cellSpan; // px within the row (0..cellSpan)
        if (ly < lo || ly > hi) continue; // outside the symbol band (covers the row gap)
        for (let px = 0; px < sw; px++) {
          const gx = ((px + 0.5) / sw) * gridW;
          const c = Math.min(cols - 1, Math.floor(gx / cellSpan));
          const lx = gx - c * cellSpan;
          if (lx < lo || lx > hi) continue; // outside the symbol band (covers the column gap)
          const idx = r * cols + c;
          tilePx[idx]!++;
          if (data[(py * sw + px) * 4 + 3]! < 40) tileClear[idx]!++;
        }
      }
      return tileClear.map((n, i) => (tilePx[i]! > 0 ? n / tilePx[i]! : 0));
    }

    // Pop any tile whose symbol patch crossed the reveal threshold: the symbol pops
    // in with a dust puff + a pitch-rising tick, the tile counts as done, and finding
    // all 3 prize symbols ends the round immediately — no need to keep scraping a card
    // you already won. The round otherwise finishes once poppedCount === rows*cols.
    function checkTilePops(perTile: number[]) {
      for (const tile of tiles) {
        if (tile.popped || (perTile[tile.index] ?? 0) < TILE_POP_THRESHOLD) continue;
        tile.popped = true;
        tile.popping = true;
        tile.popElapsed = 0;
        poppedCount++;
        playSfx('tilePop', poppedCount);
        buzz(10);
        const gap = tileSize * GAP_RATIO;
        const r = Math.floor(tile.index / cols);
        const c = tile.index % cols;
        spawnFlakes(
          gridOriginX + c * (tileSize + gap) + tileSize / 2,
          gridOriginY + r * (tileSize + gap) + tileSize / 2,
          0,
          -0.1,
          12,
        );
        if (artOn()) {
          const c = tileCentre(tile.index);
          for (let g = 0; g < POP_GLINTS; g++) {
            const ang = (g / POP_GLINTS) * Math.PI * 2 + Math.random();
            const d = tileSize * (0.22 + Math.random() * 0.2);
            spawnGlint(c.x + Math.cos(ang) * d, c.y + Math.sin(ang) * d, tileSize * (0.24 + Math.random() * 0.16), g * 40);
          }
        }
        if (tile.win) {
          prizePopped++;
          if (prizePopped >= 3 && !revealStarted) {
            buzz([20, 30, 50]);
            beginFinish(FOIL_FADE_MS);
          }
        }
      }
    }

    function showFoil() {
      foilActive = true;
      foilIdle = false;
      foilFading = false;
      foilFadeElapsed = 0;
      revealStarted = false;
      moveCounter = 0;
      poppedCount = 0;
      prizePopped = 0;
      flakes.length = 0;
      if (foilCanvas) {
        foilCanvas.style.opacity = '1';
        foilCanvas.style.pointerEvents = 'auto';
      }
    }

    function hideFoil() {
      foilActive = false;
      foilIdle = false;
      foilFading = false;
      if (foilCanvas) {
        foilCanvas.style.opacity = '0';
        foilCanvas.style.pointerEvents = 'none';
      }
    }

    function beginFinish(fadeMs: number) {
      if (revealStarted) return;
      revealStarted = true;
      clearIdleTimer();
      scratching = false;
      scratchPointerId = null;
      if (foilCanvas) foilCanvas.style.pointerEvents = 'none';
      foilFading = true;
      foilFadeElapsed = 0;
      foilFadeMs = Math.max(
        1,
        stateRef.current.turbo ? fadeMs * TURBO_DURATION_SCALE : fadeMs,
      );
      foilFadeFrom = foilCanvas ? Number(foilCanvas.style.opacity || '1') || 1 : 1;
      startWinEffects();
      playSfx('reveal');
      // Wall-clock failsafe: the fade + reveal-complete normally settle in the
      // ticker, but a frozen rAF would otherwise leave the round busy forever.
      const env = stateRef.current.envelope;
      clearFinishTimer();
      finishTimer = setTimeout(() => {
        if (foilActive || foilFading) {
          foilFading = false;
          hideFoil();
        }
        if (env) fireRevealOnce(env.roundId);
      }, foilFadeMs + 1200);
    }

    function startWinEffects() {
      for (const tile of tiles) {
        if (tile.win) {
          tile.winPlaying = true;
          tile.winElapsed = 0;
        }
        // a small open-pop for every tile as the foil clears — tiles the player
        // already scratched open keep their settled scale instead of re-popping
        if (!tile.popped) {
          tile.popping = true;
          tile.popElapsed = 0;
        }
      }
    }

    function fireRevealOnce(roundId: string) {
      if (firedRoundId === roundId) return;
      firedRoundId = roundId;
      stateRef.current.onRevealComplete();
    }

    // --- Pointer scratch handlers (on the foil canvas) ------------------------

    function localPoint(e: PointerEvent): { x: number; y: number } {
      const rect = (foilCanvas as HTMLCanvasElement).getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }

    function onFoilDown(e: PointerEvent) {
      if (revealStarted || !foilActive || autoScratch) return;
      const st = stateRef.current;
      if (st.phase === 'idle' || !st.envelope) return;
      scratching = true;
      scratchPointerId = e.pointerId;
      armIdleTimer();
      const p = localPoint(e);
      coinOver = true;
      coinX = p.x;
      coinY = p.y;
      lastScratchX = p.x;
      lastScratchY = p.y;
      eraseLine(p.x, p.y, p.x, p.y);
      // first contact chips the foil before any drag
      spawnFlakes(p.x, p.y + 3, 0, 0, 5);
      try {
        (foilCanvas as HTMLCanvasElement).setPointerCapture(e.pointerId);
      } catch {
        // capture is best-effort
      }
      e.preventDefault();
    }

    function onFoilMove(e: PointerEvent) {
      const p = localPoint(e);
      const hoverDx = p.x - coinX;
      coinOver = true;
      coinX = p.x;
      coinY = p.y;
      if (autoScratch) return;
      if (!scratching || revealStarted) {
        coinTiltTarget = clamp(hoverDx * 0.012, -0.18, 0.18);
        return;
      }
      if (scratchPointerId != null && e.pointerId !== scratchPointerId) return;
      const segX = p.x - lastScratchX;
      const segY = p.y - lastScratchY;
      const segLen = Math.hypot(segX, segY);
      coinTiltTarget = clamp(segX * 0.02, -0.32, 0.32);
      eraseLine(lastScratchX, lastScratchY, p.x, p.y);
      // shavings shed from the coin's scraping edge at the contact point
      spawnFlakes(
        p.x,
        p.y + 3,
        segX,
        segY,
        Math.min(FLAKE_PER_MOVE_MAX, Math.round(segLen * FLAKE_PER_PX)),
      );
      lastScratchX = p.x;
      lastScratchY = p.y;
      armIdleTimer();

      const now = performance.now();
      if (now - lastScratchSfx > SCRATCH_SFX_THROTTLE_MS) {
        lastScratchSfx = now;
        playSfx('scratch');
      }

      moveCounter++;
      if (moveCounter % SAMPLE_EVERY_MOVES === 0) {
        checkTilePops(erasedFractions());
        if (!revealStarted && poppedCount >= rows * cols) {
          beginFinish(FOIL_FADE_MS);
        }
      }
      e.preventDefault();
    }

    function onFoilUp(e: PointerEvent) {
      if (e.pointerType === 'touch') coinOver = false;
      if (scratchPointerId != null && e.pointerId !== scratchPointerId) return;
      scratching = false;
      scratchPointerId = null;
      // a final sample on release in case the last stroke completed the card
      if (!revealStarted && foilActive) {
        checkTilePops(erasedFractions());
        if (!revealStarted && poppedCount >= rows * cols) {
          beginFinish(FOIL_FADE_MS);
        }
      }
    }

    function onFoilEnter(e: PointerEvent) {
      const p = localPoint(e);
      coinOver = true;
      coinX = p.x;
      coinY = p.y;
    }

    function onFoilLeave() {
      if (scratching) return; // pointer capture keeps the coin glued mid-drag
      coinOver = false;
      coinTiltTarget = 0;
    }

    function attachFoilListeners() {
      const c = foilCanvasRef.current;
      if (!c) return;
      c.addEventListener('pointerdown', onFoilDown);
      c.addEventListener('pointermove', onFoilMove);
      c.addEventListener('pointerup', onFoilUp);
      c.addEventListener('pointercancel', onFoilUp);
      c.addEventListener('pointerenter', onFoilEnter);
      c.addEventListener('pointerleave', onFoilLeave);
    }

    function detachFoilListeners() {
      const c = foilCanvasRef.current;
      if (!c) return;
      c.removeEventListener('pointerdown', onFoilDown);
      c.removeEventListener('pointermove', onFoilMove);
      c.removeEventListener('pointerup', onFoilUp);
      c.removeEventListener('pointercancel', onFoilUp);
      c.removeEventListener('pointerenter', onFoilEnter);
      c.removeEventListener('pointerleave', onFoilLeave);
    }

    syncFoilSize();
    attachFoilListeners();


    function autoScratchPath(): [number, number][] {
      const gap = tileSize * GAP_RATIO;
      const pts: [number, number][] = [];
      const left = gridOriginX + tileSize * 0.12;
      const right = gridOriginX + gridW - tileSize * 0.12;
      for (let r = 0; r < rows; r++) {
        const cy = gridOriginY + r * (tileSize + gap) + tileSize / 2;
        for (const [k, dy] of [[0, -0.16], [1, 0.16]] as const) {
          const y = cy + dy * tileSize;
          const forward = (r * 2 + k) % 2 === 0;
          const n = 10;
          for (let i = 0; i <= n; i++) {
            const t = forward ? i / n : 1 - i / n;
            pts.push([left + (right - left) * t, y + Math.sin(i * 1.7 + r) * tileSize * 0.05]);
          }
        }
      }
      return pts;
    }

    function startAutoScratch() {
      if (!foilActive || revealStarted || autoScratch) return;
      if (reduced || !foilCtx) {
        beginFinish(FOIL_FADE_MS);
        return;
      }
      clearIdleTimer();
      const path = autoScratchPath();
      autoScratch = { path, elapsed: 0, duration: stateRef.current.turbo ? AUTO_SCRATCH_TURBO_MS : AUTO_SCRATCH_MS, done: 0 };
      coinOver = true;
      scratching = true;
      [coinX, coinY] = path[0]!;
      [lastScratchX, lastScratchY] = path[0]!;
      if (foilCanvas) foilCanvas.style.pointerEvents = 'none';
    }

    function stepAutoScratch(dt: number) {
      const a = autoScratch;
      if (!a) return;
      a.elapsed += dt;
      const p = Math.min(1, a.elapsed / a.duration);
      const at = p * (a.path.length - 1);
      const i = Math.floor(at);
      const f = at - i;
      const [x0, y0] = a.path[i]!;
      const [x1, y1] = a.path[Math.min(a.path.length - 1, i + 1)]!;
      const x = x0 + (x1 - x0) * f;
      const y = y0 + (y1 - y0) * f;
      const segX = x - lastScratchX;
      const segY = y - lastScratchY;
      eraseLine(lastScratchX, lastScratchY, x, y);
      spawnFlakes(x, y + 3, segX, segY, Math.min(FLAKE_PER_MOVE_MAX, Math.round(Math.hypot(segX, segY) * FLAKE_PER_PX)));
      coinTiltTarget = clamp(segX * 0.02, -0.32, 0.32);
      coinX = x;
      coinY = y;
      lastScratchX = x;
      lastScratchY = y;
      const now = performance.now();
      if (now - lastScratchSfx > SCRATCH_SFX_THROTTLE_MS) {
        lastScratchSfx = now;
        playSfx('scratch');
      }
      a.done++;
      if (a.done % 4 === 0) checkTilePops(erasedFractions());
      if (p >= 1 || revealStarted) {
        autoScratch = null;
        scratching = false;
        coinOver = false;
        if (!revealStarted) {
          checkTilePops(erasedFractions());
          beginFinish(FOIL_FADE_MS);
        }
      }
    }

    revealAllRef.current = () => {
      if (foilActive && !revealStarted) startAutoScratch();
    };

    // --- Round / idle building ------------------------------------------------

    function buildRound(roundKey: string, flat: string[], win: boolean, prize?: string) {
      destroyTiles();
      reduced = prefersReducedMotion();
      rows = presenterRef.current.rows;
      cols = presenterRef.current.cols;
      builtRoundKey = roundKey;
      firedRoundId = null;

      computeLayout();
      for (let i = 0; i < flat.length; i++) {
        const symbol = flat[i];
        const isWin = win && prize != null && symbol === prize;
        const tile = buildTile(i, symbol, isWin, tileSize);
        tiles.push(tile);
        gridLayer.addChild(tile.container);
      }
      relayout();

      // Already-revealed round (e.g. a resize rebuild after reveal): show open
      // tiles, no foil, mark fired so it never re-fires.
      if (stateRef.current.phase === 'revealed') {
        revealStarted = true;
        firedRoundId = roundKey.replace(/^play:/, '');
        for (const tile of tiles) {
          if (tile.win) {
            tile.winPlaying = true;
            tile.winElapsed = WIN_POP_DURATION_MS;
            tile.winSfxPlayed = true;
          }
        }
        hideFoil();
        clearIdleTimer();
        clearFinishTimer();
        return;
      }

      // Fresh playable round: paint and show the foil. Manual scratch is the
      // core interaction and is always available; reduced-motion only tones
      // down decoration (pop easing, sparkles), never the scratch-off itself.
      // The idle auto-reveal and "Reveal all" button remain the safety nets.
      syncFoilSize();
      paintFoil();
      showFoil();

      clearFinishTimer();
      armIdleTimer();
    }

    function buildIdle() {
      destroyTiles();
      reduced = prefersReducedMotion();
      rows = presenterRef.current.rows;
      cols = presenterRef.current.cols;
      builtRoundKey = 'idle';
      firedRoundId = null;
      revealStarted = false;
      clearIdleTimer();
      clearFinishTimer();
      hideFoil();

      computeLayout();
      const fallback = presenterRef.current;
      const total = rows * cols;
      for (let i = 0; i < total; i++) {
        const symbol =
          fallback.symbols[i % Math.max(fallback.symbols.length, 1)] ?? 'star';
        const tile = buildTile(i, symbol, false, tileSize);
        tile.symbolGfx.alpha = 0.18;
        tile.panel.alpha = 0.6;
        tiles.push(tile);
        gridLayer.addChild(tile.container);
      }
      relayout();
      // With the art, the waiting ticket is the card's: whole silver foil, corner curled, not yet scratchable.
      if (artOn()) {
        syncFoilSize();
        paintFoil(false);
        foilIdle = true;
        if (foilCanvas) {
          foilCanvas.style.opacity = '1';
          foilCanvas.style.pointerEvents = 'none';
        }
      }
    }

    // --- Win effect drawing ---------------------------------------------------

    function drawWinRing(tile: Tile, intensity: number) {
      tile.ring.clear();
      if (intensity <= 0) return;
      const size = tileSize;
      const radius = size * 0.16;
      tile.ring
        .roundRect(-size / 2, -size / 2, size, size, radius)
        .stroke({
          width: Math.max(2, size * 0.05),
          color: lighten(primary, 0.2),
          alpha: 0.85 * intensity,
        });
      tile.ring
        .roundRect(
          -size / 2 - size * 0.04,
          -size / 2 - size * 0.04,
          size + size * 0.08,
          size + size * 0.08,
          radius * 1.3,
        )
        .stroke({
          width: Math.max(1, size * 0.025),
          color: primary,
          alpha: 0.4 * intensity,
        });
    }

    function drawSparkles(tile: Tile, p: number) {
      tile.sparkleGfx.clear();
      if (p <= 0 || p >= 1) return;
      const size = tileSize;
      for (const s of tile.sparkles) {
        const local = Math.min(1, Math.max(0, (p - s.delay) / (1 - s.delay)));
        if (local <= 0) continue;
        const dist = s.dist * easeOutQuad(local);
        const fade = local < 0.5 ? local * 2 : (1 - local) * 2;
        const sx = Math.cos(s.angle) * dist;
        const sy = Math.sin(s.angle) * dist;
        const sr = size * 0.06 * (1 - local * 0.4);
        tile.sparkleGfx
          .poly([sx, sy - sr, sx + sr * 0.5, sy, sx, sy + sr, sx - sr * 0.5, sy])
          .fill({ color: lighten(primary, 0.3), alpha: 0.9 * fade });
      }
    }

    // --- Tick -----------------------------------------------------------------

    const tick = (tk: Ticker) => {
      const dt = tk.deltaMS;
      const st = stateRef.current;
      const ph = st.phase;
      const env = st.envelope;
      idlePhaseTime += dt;

      const presentation = env ? readPresentation(env.outcome.presentation) : null;
      const showGrid = ph !== 'idle' && presentation !== null;

      const roundKey = showGrid && env ? `play:${env.roundId}` : 'idle';
      if (roundKey !== builtRoundKey) {
        if (showGrid && env && presentation) {
          const flat = presentation.grid.flat();
          const fallback = presenterRef.current;
          const total = fallback.rows * fallback.cols;
          const filled =
            flat.length > 0
              ? flat
              : Array.from(
                  { length: total },
                  (_, i) =>
                    fallback.symbols[i % Math.max(fallback.symbols.length, 1)] ?? '',
                );
          buildRound(roundKey, filled, presentation.win, presentation.prizeSymbol);
        } else {
          buildIdle();
        }
      }

      idleSheen.visible = ph === 'idle';

      drawOverlay(dt);
      stepBurst(dt);

      // foil fade-out → fully hidden
      if (foilFading) {
        foilFadeElapsed += dt;
        const p = Math.min(1, foilFadeElapsed / foilFadeMs);
        const op = foilFadeFrom * (1 - p);
        if (foilCanvas) foilCanvas.style.opacity = String(op);
        if (p >= 1) {
          foilFading = false;
          hideFoil();
        }
      }

      // advance pop + win animations
      for (const tile of tiles) {
        if (tile.popping) {
          tile.popElapsed += dt;
          const dur = tile.win ? WIN_POP_DURATION_MS : 320;
          const p = Math.min(1, tile.popElapsed / dur);
          const start = 0.82;
          const scale = reduced
            ? start + (1 - start) * easeOutQuad(p)
            : start + (1 - start) * easeOutBack(p);
          tile.container.scale.set(scale);
          if (p >= 1) {
            tile.popping = false;
            tile.container.scale.set(1);
          }
        }

        if (tile.winPlaying) {
          tile.winElapsed += dt;
          if (!tile.winSfxPlayed && tile.winElapsed >= WIN_POP_DURATION_MS * 0.3) {
            tile.winSfxPlayed = true;
            playSfx('coin');
            spawnBurst(tile);
          }
          const wp = tile.winElapsed / (SPARKLE_LIFE_MS + 200);
          if (!tile.glow) drawWinRing(tile, Math.min(1, tile.winElapsed / 200));
          if (!reduced && !tile.glow) {
            drawSparkles(tile, Math.min(1, tile.winElapsed / SPARKLE_LIFE_MS));
          }
          if (wp >= 1) {
            drawSparkles(tile, 1);
          }
        }
      }

      // sustain win ring glow after the burst for winning tiles
      const cardWon = revealStarted && tiles.some((tl) => tl.win);
      for (const tile of tiles) {
        if (tile.win && revealStarted) {
          const pulse = 0.5 + 0.5 * Math.sin(idlePhaseTime / 480 + tile.index);
          if (tile.glow) {
            // The card's way: the winner glows warm and keeps hopping on its spot.
            tile.glow.visible = true;
            tile.glow.alpha = Math.min(1, tile.winElapsed / 200) * (0.5 + 0.25 * pulse);
            const hop = reduced ? 0 : Math.abs(Math.sin(idlePhaseTime / 260 + tile.index * 0.7));
            tile.symbolGfx.scale.set(1 + 0.07 * hop);
            tile.symbolGfx.y = -tileSize * 0.035 * hop;
          } else {
            drawWinRing(tile, 0.55 + 0.35 * pulse);
          }
        } else if (cardWon && tile.glow) {
          const k = Math.min(1, dt / 220);
          tile.symbolGfx.alpha += (WIN_DIM_ALPHA - tile.symbolGfx.alpha) * k;
          tile.symbolGfx.scale.set(tile.symbolGfx.scale.x + (WIN_DIM_SCALE - tile.symbolGfx.scale.x) * k);
        }
      }

      // fire reveal-complete once per round, once the foil has fully cleared
      if (showGrid && env && revealStarted && !foilActive) {
        fireRevealOnce(env.roundId);
      }

      // idle sheen sweep across the grid
      if (ph === 'idle') {
        idleSheen.clear();
        const sweep = ((idlePhaseTime / 2600) % 1) * (gridW + tileSize * 2) - tileSize;
        const bandW = tileSize * 0.7;
        idleSheen
          .poly([
            sweep,
            0,
            sweep + bandW,
            0,
            sweep + bandW - tileSize * 0.4,
            gridH,
            sweep - tileSize * 0.4,
            gridH,
          ])
          .fill({ color: 0xffffff, alpha: 0.07 });
        idleSheen.position.set(gridLayer.x, gridLayer.y);
      }
    };

    // Pixi renderer resize. Tile panels/symbols are baked at build-time
    // tileSize, so a material size change must rebuild (drops in-progress
    // scratch, fine on this square capped host; the revealed case is preserved
    // because buildRound re-opens tiles when phase === 'revealed'). A negligible
    // change just repositions and re-fits the foil.
    const onResize = () => {
      const w = app.screen.width;
      const h = app.screen.height;
      const margin = artOn() ? FRAME_MARGIN_TILES * 2 : 0;
      const cell = Math.min(
        w / (cols + (cols - 1) * GAP_RATIO + margin),
        h / (rows + (rows - 1) * GAP_RATIO + margin),
      );
      const nextSize = Math.max(32, Math.floor(cell));
      if (Math.abs(nextSize - tileSize) > 1) {
        builtRoundKey = null; // tick rebuilds at the new size next frame
        return;
      }
      relayout();
      syncFoilSize();
      if (foilActive && !revealStarted) {
        paintFoil();
        if (foilCanvas) foilCanvas.style.opacity = '1';
      } else if (foilIdle) {
        paintFoil(false);
      }
    };
    app.renderer.on('resize', onResize);

    app.ticker.add(tick);

    void preloadPieces('scratch', SCRATCH_PIECES, preferredDensity(window.devicePixelRatio)).then((imgs) => {
      if (!imgs || disposed) return;
      artImgs = imgs;
      tex = {} as Record<ScratchPiece, Texture>;
      for (const piece of SCRATCH_PIECES) tex[piece] = Texture.from({ resource: imgs[piece], autoGenerateMipmaps: true }, true);
      // Rebuild with the art unless a card is being scratched (it would lose the scratching).
      if (!scratching && moveCounter === 0) builtRoundKey = null;
    });

    return () => {
      disposed = true;
      if (tex) for (const t3d of Object.values(tex)) t3d.destroy(true);
      tex = null;
      app.ticker.remove(tick);
      app.renderer.off('resize', onResize);
      detachFoilListeners();
      clearIdleTimer();
      clearFinishTimer();
      destroyTiles();
    };
  }).current;

  // Start fetching the art with the component, ahead of the Pixi init the scene waits on.
  useEffect(() => {
    void preloadPieces('scratch', SCRATCH_PIECES, preferredDensity(window.devicePixelRatio));
  }, []);

  const hostRef = usePixiScene(onReady);

  return (
    <div className="relative w-full lg:min-h-[clamp(340px,60vh,560px)] originals-scene-fit [--orig-reserve:600px] overflow-hidden">
      <div className="absolute inset-0 touch-none">
        <div ref={hostRef} className="absolute inset-0" aria-hidden />
        <canvas
          ref={foilCanvasRef}
          className="absolute inset-0 z-10 touch-none"
          style={{
            width: '100%',
            height: '100%',
            pointerEvents: 'auto',
            cursor: 'none',
          }}
          aria-hidden
        />
        <canvas
          ref={flakesCanvasRef}
          className="absolute inset-0 z-20 pointer-events-none"
          style={{ width: '100%', height: '100%' }}
          aria-hidden
        />
      </div>
      {/* Sibling of the square host (not a child) so it overlays the bottom band
          of the scene wrapper without sitting on top of the foil — keeping the
          whole card surface scratchable. */}
      {phase === 'playing' && (
        <button
          type="button"
          onClick={() => revealAllRef.current()}
          className="absolute bottom-2 left-1/2 z-20 -translate-x-1/2 rounded-full bg-[var(--primary)] px-5 py-2 text-[12px] font-bold uppercase tracking-wide text-[var(--primary-text)] shadow-[0_4px_14px_var(--primary-glow)] transition-colors hover:bg-[var(--primary-hover)] active:bg-[var(--primary-press)]"
        >
          {revealAllLabel}
        </button>
      )}
      {phase === 'idle' && (
        <span className="absolute bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('scratch.reveal')}
        </span>
      )}
    </div>
  );
}
